'use client';

import { ArrowLeft, Check, Undo2, X } from 'lucide-react';
import type { ReactNode, Ref } from 'react';
import styles from './modal.module.css';

interface ModalShellProps {
	title: string;
	icon?: ReactNode;
	headerAction?: ReactNode;
	onClose: () => void;
	onCancel?: () => void;
	children?: ReactNode;
	newCount?: number;
	editedCount?: number;
	saveText?: string;
	cancelText?: string;
	formId?: string;
	maxWidth?: string;
	/** Ancho mínimo de la tarjeta (nunca más que el 94% del viewport, para no salirse en un teléfono). */
	minWidth?: string;
	minHeight?: string;
	/** Tope de alto respecto al viewport (default 90vh). */
	maxViewportHeight?: string;
	bodyOverflowY?: 'auto' | 'visible';
	bodyRef?: Ref<HTMLDivElement>;
	saveDisabled?: boolean;
	showSaveAction?: boolean;
	showCancelAction?: boolean;
	showCloseAction?: boolean;
	/** Tono del botón cancelar/cerrar. `brand` = verde institucional (secciones que
	 *  persisten inline, donde "Cerrar" equivale a confirmar). Default: neutral. */
	cancelTone?: 'neutral' | 'brand';
	footerIndicatorText?: string;
	footerIndicatorContent?: ReactNode;
	footerHasChanges?: boolean;
	/** Oculta el indicador de cambios (círculo + texto) del footer. */
	hideFooterIndicator?: boolean;
	/** Modo embebido: renderiza sólo el interior (header/body/footer) sin overlay ni card propio.
	 *  El contenedor padre (p. ej. el hub de identidad) provee el card y controla el tamaño. */
	embedded?: boolean;
	/** Si se define, muestra un botón "←" a la izquierda del título (volver atrás). */
	onBack?: () => void;
	/** Si se define, muestra el botón "deshacer cambios" (flecha-atrás) junto a las acciones.
	 *  Abre el flujo de reversión al checkpoint con que se abrió el modal. */
	onUndo?: () => void;
	/** Texto accesible del botón deshacer (default "Deshacer cambios"). */
	undoText?: string;
	/** Deshabilita el botón deshacer (p. ej. cuando no hay cambios o se está revirtiendo). */
	undoDisabled?: boolean;
	/** Optional data-tutorial id on the modal card (spotlight tour anchor). */
	dataTutorial?: string;
}

export function ModalShell({
	title,
	icon,
	headerAction,
	onClose,
	onCancel,
	children,
	newCount = 0,
	editedCount = 0,
	saveText = 'Guardar',
	cancelText = 'Cancelar',
	formId,
	maxWidth,
	minWidth,
	minHeight,
	maxViewportHeight = '90vh',
	bodyOverflowY = 'auto',
	bodyRef,
	saveDisabled = false,
	showSaveAction = true,
	showCancelAction = true,
	showCloseAction = true,
	cancelTone = 'neutral',
	footerIndicatorText,
	footerIndicatorContent,
	footerHasChanges,
	hideFooterIndicator = false,
	embedded = false,
	onBack,
	onUndo,
	undoText = 'Deshacer cambios',
	undoDisabled = false,
	dataTutorial,
}: ModalShellProps) {
	const hasChanges = footerHasChanges ?? (newCount > 0 || editedCount > 0);
	const showFooter = !hideFooterIndicator || showCancelAction || showSaveAction;

	const indicatorText = footerIndicatorText ?? (
		newCount > 0 && editedCount > 0
			? `${newCount} nuevos · ${editedCount} editados`
			: newCount > 0
				? `${newCount} nuevo(s)`
				: editedCount > 0
					? `${editedCount} editado(s)`
					: 'Sin cambios'
	);

	const inner = (
		<>
			<div className={styles.modalHeader}>
				<div className={styles.modalHeaderLeft}>
					{onBack && (
						<button aria-label="Volver" className={styles.modalBackBtn} onClick={onBack} type="button">
							<ArrowLeft size={18} />
						</button>
					)}
					<span className={styles.modalBar} />
					{icon ? <span className={styles.modalIcon}>{icon}</span> : null}
					<p className={styles.modalHeaderTitle}>{title}</p>
				</div>
				<div className={styles.modalHeaderRight}>
					{headerAction}
					{showCloseAction ? (
						<button aria-label="Cerrar modal" className={styles.modalCloseIconBtn} onClick={onClose} type="button">
							<X size={18} />
						</button>
					) : null}
				</div>
			</div>
			<div ref={bodyRef} className={styles.modalBodyContent} style={{ overflowY: bodyOverflowY }}>
				{children ?? <p className={styles.modalBodyPlaceholder}>Modal en construcción.</p>}
			</div>
			{showFooter ? (
				<div className={styles.modalFooter}>
					{hideFooterIndicator ? (
						<span />
					) : (
						<div className={styles.modalFooterIndicator}>
							<span
								className={`${styles.indicatorCircle} ${hasChanges ? styles.indicatorActive : styles.indicatorEmpty}`}
							/>
							{footerIndicatorContent ?? (
								<span className={`${styles.indicatorText} ${hasChanges ? styles.indicatorTextActive : ''}`}>{indicatorText}</span>
							)}
						</div>
					)}
					<div className={styles.modalActions}>
						{onUndo ? (
							<button
								className={styles.btnUndo}
								onClick={onUndo}
								type="button"
								disabled={undoDisabled}
								aria-label={undoText}
								title={undoText}
								data-tutorial="modal-undo"
							>
								<Undo2 size={16} aria-hidden="true" />
								<span>{undoText}</span>
							</button>
						) : null}
						{showCancelAction ? (
							<button
								className={`${styles.btnSecondary} ${cancelTone === 'brand' ? styles.btnSecondaryBrand : ''}`}
								onClick={onCancel ?? onClose}
								type="button"
							>
								{cancelTone === 'brand' ? <Check size={16} aria-hidden="true" /> : null}
								{cancelText}
							</button>
						) : null}
						{showSaveAction ? (
							<button className={styles.btnPrimary} form={formId} type={formId ? 'submit' : 'button'} disabled={saveDisabled}>
								{saveText}
							</button>
						) : null}
					</div>
				</div>
			) : null}
		</>
	);

	// Embebido: el padre provee el overlay + card (y controla el tamaño / la animación).
	if (embedded) {
		return inner;
	}

	return (
		<div className={styles.modalOverlay}>
			<div
				className={styles.modalCard}
				data-tutorial={dataTutorial}
				onClick={(e) => e.stopPropagation()}
				style={{
					...(maxWidth ? { maxWidth } : {}),
					...(minWidth ? { minWidth: `min(${minWidth}, 94vw)` } : {}),
					...(minHeight ? { minHeight: `min(${minHeight}, ${maxViewportHeight})`, height: `min(${minHeight}, ${maxViewportHeight})` } : {}),
				}}
			>
				{inner}
			</div>
		</div>
	);
}
